import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { ArrowLeft, PlusCircle, Trash2 } from 'lucide-react';
import { createProposal, getProposal, updateProposal } from '../utils/api';

const CreateProposal = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    risk_profile: 'Moderate',
    investment_horizon: '6 months',
    investment_objective: '',
    financial_goals: '',
    lumpsum_investment: '',
    sip_per_month: '',
    daily_sip: '',
    stp_per_month: '',
    swp_per_month: '',
    notes: '',
    recommendations: [
      {
        scheme_name: '',
        amc: '',
        category: 'Equity',
        investment_type: 'SIP',
        sip_frequency: 'Monthly',
        stp_frequency: 'Monthly',
        switch_out_scheme: '',
        switch_in_scheme: '',
        initial_lumpsum: '',
        source_return: '6',
        investment_amount: '',
        expected_return: '',
        tenure_months: '',
        holding_period_months: '',
        fund_inception_date: '',
        exit_load_percent: '',
        exit_load_days: '',
        target_scheme_name: '',
        returns_1y: '',
        returns_3y: '',
        returns_5y: '',
        returns_since_inception: '',
        benchmark_name: '',
        bench_1y: '',
        bench_3y: '',
        bench_5y: '',
        bench_since_inception: '',
        source_fund_inception_date: '',
        source_scheme_name: '',
        source_exit_load_percent: '',
        source_exit_load_days: '',
        source_returns_1y: '',
        source_returns_3y: '',
        source_returns_5y: '',
        source_returns_since_inception: '',
        source_benchmark_name: '',
        source_bench_1y: '',
        source_bench_3y: '',
        source_bench_5y: '',
        source_bench_since_inception: ''
      }
    ]
  });

  // Load existing proposal when in edit mode
  useEffect(() => {
    if (!isEditMode) return;
    getProposal(id).then((proposal) => {
    // If STP recommendation used the old "source → target" combined format, split it back
    const normalisedRecs = (proposal.recommendations || []).map(rec => ({
      scheme_name: rec.scheme_name || '',
      amc: rec.amc || '',
      category: rec.category || 'Equity',
      investment_type: rec.investment_type || 'SIP',
      sip_frequency: rec.sip_frequency || 'Monthly',
      stp_frequency: rec.stp_frequency || 'Monthly',
      switch_out_scheme: rec.switch_out_scheme || '',
      switch_in_scheme: rec.switch_in_scheme || '',
      initial_lumpsum: rec.initial_lumpsum != null ? String(rec.initial_lumpsum) : '',
      source_return: rec.source_return != null ? String(rec.source_return) : '6',
      investment_amount: rec.investment_amount != null ? String(rec.investment_amount) : '',
      expected_return: rec.expected_return != null ? String(rec.expected_return) : '',
      tenure_months: rec.tenure_months != null ? String(rec.tenure_months) : '',
      holding_period_months: rec.holding_period_months != null ? String(rec.holding_period_months) : '',
      fund_inception_date: rec.fund_inception_date || '',
      exit_load_percent: rec.exit_load_percent != null ? String(rec.exit_load_percent) : '',
      exit_load_days: rec.exit_load_days != null ? String(rec.exit_load_days) : '',
      expense_ratio: rec.expense_ratio != null ? String(rec.expense_ratio) : '',
      trail_commission: rec.trail_commission != null ? String(rec.trail_commission) : '',
      target_scheme_name: rec.target_scheme_name || '',
      returns_1y: rec.returns_1y != null ? String(rec.returns_1y) : '',
      returns_3y: rec.returns_3y != null ? String(rec.returns_3y) : '',
      returns_5y: rec.returns_5y != null ? String(rec.returns_5y) : '',
      returns_since_inception: rec.returns_since_inception != null ? String(rec.returns_since_inception) : '',
      benchmark_name: rec.benchmark_name || '',
      bench_1y: rec.bench_1y != null ? String(rec.bench_1y) : '',
      bench_3y: rec.bench_3y != null ? String(rec.bench_3y) : '',
      bench_5y: rec.bench_5y != null ? String(rec.bench_5y) : '',
      bench_since_inception: rec.bench_since_inception != null ? String(rec.bench_since_inception) : '',
      source_fund_inception_date: rec.source_fund_inception_date || '',
      source_scheme_name: rec.source_scheme_name || '',
      source_exit_load_percent: rec.source_exit_load_percent != null ? String(rec.source_exit_load_percent) : '',
      source_exit_load_days: rec.source_exit_load_days != null ? String(rec.source_exit_load_days) : '',
      source_expense_ratio: rec.source_expense_ratio != null ? String(rec.source_expense_ratio) : '',
      source_returns_1y: rec.source_returns_1y != null ? String(rec.source_returns_1y) : '',
      source_returns_3y: rec.source_returns_3y != null ? String(rec.source_returns_3y) : '',
      source_returns_5y: rec.source_returns_5y != null ? String(rec.source_returns_5y) : '',
      source_returns_since_inception: rec.source_returns_since_inception != null ? String(rec.source_returns_since_inception) : '',
      source_benchmark_name: rec.source_benchmark_name || '',
      source_bench_1y: rec.source_bench_1y != null ? String(rec.source_bench_1y) : '',
      source_bench_3y: rec.source_bench_3y != null ? String(rec.source_bench_3y) : '',
      source_bench_5y: rec.source_bench_5y != null ? String(rec.source_bench_5y) : '',
      source_bench_since_inception: rec.source_bench_since_inception != null ? String(rec.source_bench_since_inception) : ''
    }));
    setFormData({
      client_name: proposal.client_name || '',
      client_email: proposal.client_email || '',
      client_phone: proposal.client_phone || '',
      risk_profile: proposal.risk_profile || 'Moderate',
      investment_horizon: proposal.investment_horizon || '6 months',
      investment_objective: proposal.investment_objective || '',
      financial_goals: proposal.financial_goals || '',
      lumpsum_investment: proposal.lumpsum_investment != null ? String(proposal.lumpsum_investment) : '',
      sip_per_month: proposal.sip_per_month != null ? String(proposal.sip_per_month) : '',
      daily_sip: proposal.daily_sip != null ? String(proposal.daily_sip) : '',
      stp_per_month: proposal.stp_per_month != null ? String(proposal.stp_per_month) : '',
      swp_per_month: proposal.swp_per_month != null ? String(proposal.swp_per_month) : '',
      notes: proposal.notes || '',
      recommendations: normalisedRecs.length > 0 ? normalisedRecs : [{
        scheme_name: '', amc: '', category: 'Equity', investment_type: 'SIP',
        sip_frequency: 'Monthly', stp_frequency: 'Monthly',
        switch_out_scheme: '', switch_in_scheme: '',
        initial_lumpsum: '', source_return: '6',
        investment_amount: '', expected_return: '', tenure_months: '', holding_period_months: '',
        fund_inception_date: '', exit_load_percent: '', exit_load_days: '', target_scheme_name: '', returns_1y: '', returns_3y: '', returns_5y: '', returns_since_inception: '',
        benchmark_name: '', bench_1y: '', bench_3y: '', bench_5y: '', bench_since_inception: '',
        source_fund_inception_date: '', source_scheme_name: '', source_exit_load_percent: '', source_exit_load_days: '', source_returns_1y: '', source_returns_3y: '', source_returns_5y: '', source_returns_since_inception: '',
        source_benchmark_name: '', source_bench_1y: '', source_bench_3y: '', source_bench_5y: '', source_bench_since_inception: ''
      }]
    });
    }).catch(() => setNotFound(true));
  }, [id, isEditMode]);

  const addRecommendation = () => {
    setFormData({
      ...formData,
      recommendations: [
        ...formData.recommendations,
        {
          scheme_name: '',
          amc: '',
          category: 'Equity',
          investment_type: 'SIP',
          sip_frequency: 'Monthly',
          stp_frequency: 'Monthly',
          switch_out_scheme: '',
          switch_in_scheme: '',
          initial_lumpsum: '',
          source_return: '6',
          investment_amount: '',
          expected_return: '',
          tenure_months: '',
          holding_period_months: '',
          fund_inception_date: '',
          exit_load_percent: '',
          exit_load_days: '',
          target_scheme_name: '',
          returns_1y: '',
          returns_3y: '',
          returns_5y: '',
          returns_since_inception: '',
          benchmark_name: '',
          bench_1y: '',
          bench_3y: '',
          bench_5y: '',
          bench_since_inception: '',
          source_fund_inception_date: '',
          source_scheme_name: '',
          source_exit_load_percent: '',
          source_exit_load_days: '',
          source_returns_1y: '',
          source_returns_3y: '',
          source_returns_5y: '',
          source_returns_since_inception: '',
          source_benchmark_name: '',
          source_bench_1y: '',
          source_bench_3y: '',
          source_bench_5y: '',
          source_bench_since_inception: ''
        }
      ]
    });
  };

  const removeRecommendation = (index) => {
    const newRecommendations = formData.recommendations.filter((_, i) => i !== index);
    setFormData({ ...formData, recommendations: newRecommendations });
  };

  const updateRecommendation = (index, field, value) => {
    const newRecommendations = [...formData.recommendations];
    newRecommendations[index][field] = value;
    setFormData({ ...formData, recommendations: newRecommendations });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // SIF minimum investment validation: 10,00,000 (Lumpsum) OR annualised (SIP*12 / Daily SIP*365)
    const SIF_MIN = 1000000; // ₹10 Lakhs
    for (let i = 0; i < formData.recommendations.length; i++) {
      const rec = formData.recommendations[i];
      if (rec.category !== 'SIF' && rec.investment_type !== 'SIF') continue;
      const amt = parseFloat(rec.investment_amount) || 0;
      const type = rec.investment_type;
      const freq = rec.sip_frequency;
      const stpFreq = rec.stp_frequency;

      let annualised = amt;
      let label = 'Lumpsum';
      if (type === 'SIP') {
        if (freq === 'Daily') { annualised = amt * 365; label = 'Daily SIP × 365'; }
        else { annualised = amt * 12; label = 'Monthly SIP × 12'; }
      } else if (type === 'STP') {
        if (stpFreq === 'Daily') { annualised = amt * 365; label = 'Daily STP × 365'; }
        else if (stpFreq === 'Weekly') { annualised = amt * 52; label = 'Weekly STP × 52'; }
        else { annualised = amt * 12; label = 'Monthly STP × 12'; }
      } else if (type === 'SWP') {
        // For SWP, validate against the initial corpus (investment_amount here is withdrawal). Use initial_lumpsum if present.
        annualised = parseFloat(rec.initial_lumpsum) || amt;
        label = 'Initial Corpus';
      } else if (type === 'STP' || type === 'Lumpsum') {
        // For STP, also check initial_lumpsum
        const il = parseFloat(rec.initial_lumpsum) || 0;
        if (il > annualised) { annualised = il; label = 'Initial Lumpsum'; }
      }

      if (annualised < SIF_MIN) {
        alert(`SIF Investment Rule Violation (Recommendation #${i + 1}):\n\nSIF requires a minimum investment of ₹10,00,000.\nCurrent ${label}: ₹${annualised.toLocaleString('en-IN')}.\n\nPlease increase the amount, or change the Investment Type / Category.`);
        return;
      }
    }

    setLoading(true);

    try {
      // Convert string numbers to actual numbers, and normalise scheme_name for STP
      const payload = {
        ...formData,
        total_investment: parseFloat(formData.total_investment),
        recommendations: formData.recommendations.map(rec => ({
          ...rec,
          // For STP: scheme_name = "<source> → <target>" so existing views still render meaningfully
          scheme_name: rec.investment_type === 'STP'
            ? `${rec.switch_out_scheme} → ${rec.switch_in_scheme}`
            : rec.scheme_name,
          investment_amount: parseFloat(rec.investment_amount),
          expected_return: parseFloat(rec.expected_return),
          tenure_months: parseInt(rec.tenure_months),
          holding_period_months: rec.holding_period_months ? parseInt(rec.holding_period_months) : parseInt(rec.tenure_months),
          initial_lumpsum: rec.initial_lumpsum ? parseFloat(rec.initial_lumpsum) : 0,
          source_return: rec.source_return ? parseFloat(rec.source_return) : 6,
          // Optional returns/benchmark fields: keep as null if empty so PDF/View can hide the section cleanly
          fund_inception_date: rec.fund_inception_date || null,
          exit_load_percent: rec.exit_load_percent !== '' ? parseFloat(rec.exit_load_percent) : null,
          exit_load_days: rec.exit_load_days !== '' ? parseInt(rec.exit_load_days, 10) : null,
          expense_ratio: (rec.expense_ratio !== '' && rec.expense_ratio != null) ? parseFloat(rec.expense_ratio) : null,
          trail_commission: (rec.trail_commission !== '' && rec.trail_commission != null) ? parseFloat(rec.trail_commission) : null,
          target_scheme_name: rec.target_scheme_name || '',
          returns_1y: rec.returns_1y !== '' ? parseFloat(rec.returns_1y) : null,
          returns_3y: rec.returns_3y !== '' ? parseFloat(rec.returns_3y) : null,
          returns_5y: rec.returns_5y !== '' ? parseFloat(rec.returns_5y) : null,
          returns_since_inception: rec.returns_since_inception !== '' ? parseFloat(rec.returns_since_inception) : null,
          benchmark_name: rec.benchmark_name || '',
          bench_1y: rec.bench_1y !== '' ? parseFloat(rec.bench_1y) : null,
          bench_3y: rec.bench_3y !== '' ? parseFloat(rec.bench_3y) : null,
          bench_5y: rec.bench_5y !== '' ? parseFloat(rec.bench_5y) : null,
          bench_since_inception: rec.bench_since_inception !== '' ? parseFloat(rec.bench_since_inception) : null,
          // STP source scheme returns (only meaningful when investment_type === 'STP')
          source_fund_inception_date: rec.source_fund_inception_date || null,
          source_scheme_name: rec.source_scheme_name || '',
          source_exit_load_percent: rec.source_exit_load_percent !== '' ? parseFloat(rec.source_exit_load_percent) : null,
          source_exit_load_days: rec.source_exit_load_days !== '' ? parseInt(rec.source_exit_load_days, 10) : null,
          source_expense_ratio: (rec.source_expense_ratio !== '' && rec.source_expense_ratio != null) ? parseFloat(rec.source_expense_ratio) : null,
          source_returns_1y: rec.source_returns_1y !== '' ? parseFloat(rec.source_returns_1y) : null,
          source_returns_3y: rec.source_returns_3y !== '' ? parseFloat(rec.source_returns_3y) : null,
          source_returns_5y: rec.source_returns_5y !== '' ? parseFloat(rec.source_returns_5y) : null,
          source_returns_since_inception: rec.source_returns_since_inception !== '' ? parseFloat(rec.source_returns_since_inception) : null,
          source_benchmark_name: rec.source_benchmark_name || '',
          source_bench_1y: rec.source_bench_1y !== '' ? parseFloat(rec.source_bench_1y) : null,
          source_bench_3y: rec.source_bench_3y !== '' ? parseFloat(rec.source_bench_3y) : null,
          source_bench_5y: rec.source_bench_5y !== '' ? parseFloat(rec.source_bench_5y) : null,
          source_bench_since_inception: rec.source_bench_since_inception !== '' ? parseFloat(rec.source_bench_since_inception) : null
        }))
      };

      if (isEditMode) {
        await updateProposal(id, payload);
        alert('Proposal updated successfully!');
        navigate(`/admin/proposals/${id}`);
      } else {
        await createProposal(payload);
        alert('Proposal created successfully!');
        navigate('/admin/dashboard');
      }
    } catch (err) {
      console.error('Error saving proposal:', err);
      alert('Error saving proposal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-700 text-lg mb-4">Proposal not found.</p>
          <Link to="/admin/dashboard">
            <Button>Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin/dashboard">
            <Button variant="outline" size="sm">
              <ArrowLeft size={16} className="mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">{isEditMode ? 'Edit Proposal' : 'Create New Proposal'}</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-6">
            {/* Client Information */}
            <Card>
              <CardHeader>
                <CardTitle>Client Information</CardTitle>
              </CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="client_name">Client Name *</Label>
                  <input
                    type="text"
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="client_email">Client Email *</Label>
                  <input
                    type="email"
                    id="client_email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="client_phone">Client Phone *</Label>
                  <input
                    type="tel"
                    id="client_phone"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="risk_profile">Risk Profile *</Label>
                  <select
                    id="risk_profile"
                    value={formData.risk_profile}
                    onChange={(e) => setFormData({ ...formData, risk_profile: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="Conservative">Conservative</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Aggressive">Aggressive</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="investment_horizon">Investment Horizon *</Label>
                  <select
                    id="investment_horizon"
                    value={formData.investment_horizon}
                    onChange={(e) => setFormData({ ...formData, investment_horizon: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="6 months">6 months</option>
                    <option value="Up to 1 year">Up to 1 year</option>
                    <option value="Short Term (1-3 years)">Short Term (1-3 years)</option>
                    <option value="Medium Term (3-5 years)">Medium Term (3-5 years)</option>
                    <option value="Long Term (5+ years)">Long Term (5+ years)</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="investment_objective">Investment Objective *</Label>
                  <textarea
                    id="investment_objective"
                    value={formData.investment_objective}
                    onChange={(e) => setFormData({ ...formData, investment_objective: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    placeholder="E.g., Wealth creation, Retirement planning, Child education"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="lumpsum_investment">Lumpsum Investment (₹)</Label>
                  <input
                    type="number"
                    id="lumpsum_investment"
                    value={formData.lumpsum_investment || ''}
                    onChange={(e) => setFormData({ ...formData, lumpsum_investment: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="500000"
                  />
                  <p className="text-xs text-slate-500 mt-1">One-time investment amount</p>
                </div>

                <div>
                  <Label htmlFor="sip_per_month">Monthly SIP (₹)</Label>
                  <input
                    type="number"
                    id="sip_per_month"
                    value={formData.sip_per_month || ''}
                    onChange={(e) => setFormData({ ...formData, sip_per_month: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="20000"
                  />
                  <p className="text-xs text-slate-500 mt-1">Monthly SIP amount</p>
                </div>

                <div>
                  <Label htmlFor="daily_sip">Daily SIP (₹)</Label>
                  <input
                    type="number"
                    id="daily_sip"
                    value={formData.daily_sip || ''}
                    onChange={(e) => setFormData({ ...formData, daily_sip: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Daily SIP amount (if any)</p>
                </div>

                <div>
                  <Label htmlFor="stp_per_month">STP Per Month (₹)</Label>
                  <input
                    type="number"
                    id="stp_per_month"
                    value={formData.stp_per_month || ''}
                    onChange={(e) => setFormData({ ...formData, stp_per_month: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="10000"
                  />
                  <p className="text-xs text-slate-500 mt-1">Monthly STP transfer (if any)</p>
                </div>

                <div>
                  <Label htmlFor="swp_per_month">SWP Per Month (₹)</Label>
                  <input
                    type="number"
                    id="swp_per_month"
                    value={formData.swp_per_month || ''}
                    onChange={(e) => setFormData({ ...formData, swp_per_month: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="10000"
                  />
                  <p className="text-xs text-slate-500 mt-1">Monthly SWP withdrawal (if any)</p>
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="financial_goals">Financial Goals *</Label>
                  <textarea
                    id="financial_goals"
                    value={formData.financial_goals}
                    onChange={(e) => setFormData({ ...formData, financial_goals: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="E.g., Retirement planning, Child's education, Wealth creation"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="notes">Additional Notes</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    placeholder="Any special instructions or notes"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Investment Recommendations */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Investment Recommendations</CardTitle>
                  <Button
                    type="button"
                    onClick={addRecommendation}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <PlusCircle size={16} />
                    Add Scheme
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {formData.recommendations.map((rec, index) => (
                  <div key={index} className="p-4 border-2 border-slate-200 rounded-lg relative">
                    {formData.recommendations.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeRecommendation(index)}
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2 text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}

                    <h4 className="font-semibold text-slate-900 mb-4">Scheme {index + 1}</h4>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Investment Type *</Label>
                        <select
                          value={rec.investment_type}
                          onChange={(e) => updateRecommendation(index, 'investment_type', e.target.value)}
                          className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="SIP">SIP (Systematic Investment Plan)</option>
                          <option value="Lumpsum">Lumpsum</option>
                          <option value="STP">STP (Systematic Transfer Plan)</option>
                          <option value="SWP">SWP (Systematic Withdrawal Plan)</option>
                          <option value="SIF">SIF (Specialized Investment Fund)</option>
                        </select>
                        {rec.investment_type === 'SIF' && (
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid={`sif-type-info-${index}`}>
                            SIF requires a minimum investment of ₹10,00,000.
                          </p>
                        )}
                      </div>

                      {rec.investment_type === 'SIP' && (
                        <div>
                          <Label>SIP Frequency *</Label>
                          <select
                            value={rec.sip_frequency || 'Monthly'}
                            onChange={(e) => updateRecommendation(index, 'sip_frequency', e.target.value)}
                            className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            <option value="Monthly">Monthly</option>
                            <option value="Daily">Daily</option>
                          </select>
                        </div>
                      )}

                      {rec.investment_type === 'STP' && (
                        <div>
                          <Label>STP Frequency *</Label>
                          <select
                            value={rec.stp_frequency || 'Monthly'}
                            onChange={(e) => updateRecommendation(index, 'stp_frequency', e.target.value)}
                            className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            <option value="Monthly">Monthly</option>
                            <option value="Weekly">Weekly</option>
                            <option value="Daily">Daily</option>
                          </select>
                        </div>
                      )}

                      {/* For STP, show Switch Out / Switch In scheme inputs */}
                      {rec.investment_type === 'STP' && (
                        <>
                          <div>
                            <Label>Switch Out Scheme *</Label>
                            <input
                              type="text"
                              value={rec.switch_out_scheme || ''}
                              onChange={(e) => updateRecommendation(index, 'switch_out_scheme', e.target.value)}
                              className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="E.g., HDFC Liquid Fund"
                              required
                            />
                            <p className="text-xs text-slate-500 mt-1">Source (usually debt/liquid)</p>
                          </div>
                          <div>
                            <Label>Switch In Scheme *</Label>
                            <input
                              type="text"
                              value={rec.switch_in_scheme || ''}
                              onChange={(e) => updateRecommendation(index, 'switch_in_scheme', e.target.value)}
                              className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="E.g., HDFC Flexi Cap Fund"
                              required
                            />
                            <p className="text-xs text-slate-500 mt-1">Destination (usually equity)</p>
                          </div>

                          <div>
                            <Label>Initial Lumpsum in Source (₹) *</Label>
                            <input
                              type="number"
                              value={rec.initial_lumpsum || ''}
                              onChange={(e) => updateRecommendation(index, 'initial_lumpsum', e.target.value)}
                              className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="500000"
                              min="0"
                              required
                            />
                            <p className="text-xs text-slate-500 mt-1">Money parked in source (debt/liquid) upfront</p>
                          </div>

                          <div>
                            <Label>Source Fund Return (% p.a.)</Label>
                            <input
                              type="number"
                              step="0.1"
                              value={rec.source_return || '6'}
                              onChange={(e) => updateRecommendation(index, 'source_return', e.target.value)}
                              className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="6"
                            />
                            <p className="text-xs text-slate-500 mt-1">Expected return on source (debt/liquid) - typically 5-7%</p>
                          </div>
                        </>
                      )}

                      {/* For SIP, Lumpsum, SWP — single scheme name */}
                      {rec.investment_type !== 'STP' && (
                        <div>
                          <Label>
                            {rec.investment_type === 'SWP' ? 'Withdrawal Scheme *' : 'Scheme Name *'}
                          </Label>
                          <input
                            type="text"
                            value={rec.scheme_name}
                            onChange={(e) => updateRecommendation(index, 'scheme_name', e.target.value)}
                            className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder={rec.investment_type === 'SWP' ? 'E.g., HDFC Balanced Advantage Fund' : 'E.g., SBI Bluechip Fund'}
                            required
                          />
                        </div>
                      )}

                      <div>
                        <Label>AMC *</Label>
                        <input
                          type="text"
                          value={rec.amc}
                          onChange={(e) => updateRecommendation(index, 'amc', e.target.value)}
                          className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="E.g., SBI Mutual Fund"
                          required
                        />
                      </div>

                      <div>
                        <Label>Category *</Label>
                        <select
                          value={rec.category}
                          onChange={(e) => updateRecommendation(index, 'category', e.target.value)}
                          className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="Equity">Equity</option>
                          <option value="Debt">Debt</option>
                          <option value="Hybrid">Hybrid</option>
                          <option value="ELSS">ELSS</option>
                          <option value="SIF">SIF (Specialized Investment Fund)</option>
                        </select>
                        {rec.category === 'SIF' && (
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid={`sif-info-${index}`}>
                            SIF requires a minimum investment of ₹10,00,000 (Lumpsum, or Annualised: SIP × 12 / Daily SIP × 365).
                          </p>
                        )}
                      </div>

                      <div>
                        <Label>
                          {(rec.investment_type === 'STP' || rec.investment_type === 'SWP')
                            ? 'Target Exit Load (%)'
                            : 'Exit Load (%)'}
                        </Label>
                        <input
                          type="number"
                          step="0.01"
                          value={rec.exit_load_percent}
                          onChange={(e) => updateRecommendation(index, 'exit_load_percent', e.target.value)}
                          className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="1.00"
                          data-testid={`exit-load-percent-${index}`}
                        />
                        <p className="text-xs text-slate-500 mt-1">Leave blank if Nil.</p>
                      </div>

                      <div>
                        <Label>
                          {(rec.investment_type === 'STP' || rec.investment_type === 'SWP')
                            ? 'Target Exit Load Applicable Within (Days)'
                            : 'Exit Load Applicable Within (Days)'}
                        </Label>
                        <input
                          type="number"
                          value={rec.exit_load_days}
                          onChange={(e) => updateRecommendation(index, 'exit_load_days', e.target.value)}
                          className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="E.g., 365"
                          data-testid={`exit-load-days-${index}`}
                        />
                      </div>

                      <div>
                        <Label>{(rec.investment_type === 'STP' || rec.investment_type === 'SWP') ? 'Target Scheme Expense Ratio (%)' : 'Expense Ratio (%)'}</Label>
                        <input
                          type="number"
                          step="0.01"
                          value={rec.expense_ratio || ''}
                          onChange={(e) => updateRecommendation(index, 'expense_ratio', e.target.value)}
                          className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., 1.25"
                          data-testid={`expense-ratio-${index}`}
                        />
                        <p className="text-xs text-slate-500 mt-1">Total Expense Ratio (Regular Plan){(rec.investment_type === 'STP' || rec.investment_type === 'SWP') ? ' of the target scheme.' : '.'}</p>
                      </div>

                      <div>
                        <Label>Trail Commission (% p.a.)</Label>
                        <input
                          type="number"
                          step="0.01"
                          value={rec.trail_commission || ''}
                          onChange={(e) => updateRecommendation(index, 'trail_commission', e.target.value)}
                          className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., 1.00"
                          data-testid={`trail-commission-${index}`}
                        />
                        <p className="text-xs text-slate-500 mt-1">Commission disclosure: trail we earn from the AMC (built into the expense ratio, not an extra charge).</p>
                      </div>

                      <div>
                        <Label>
                          {rec.investment_type === 'SIP'
                            ? (rec.sip_frequency === 'Daily' ? 'Daily SIP Amount' : 'Monthly SIP Amount')
                            : rec.investment_type === 'STP'
                              ? (rec.stp_frequency === 'Daily'
                                  ? 'Daily Transfer Amount'
                                  : rec.stp_frequency === 'Weekly'
                                    ? 'Weekly Transfer Amount'
                                    : 'Monthly Transfer Amount')
                              : rec.investment_type === 'SWP'
                                ? 'Monthly Withdrawal Amount'
                                : rec.investment_type === 'SIF'
                                  ? 'SIF Investment Amount'
                                  : 'Lumpsum Amount'} (₹) *
                        </Label>
                        <input
                          type="number"
                          value={rec.investment_amount}
                          onChange={(e) => updateRecommendation(index, 'investment_amount', e.target.value)}
                          className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={
                            rec.investment_type === 'SIP'
                              ? (rec.sip_frequency === 'Daily' ? '500' : '5000')
                              : rec.investment_type === 'STP'
                                ? (rec.stp_frequency === 'Daily' ? '1000' : rec.stp_frequency === 'Weekly' ? '5000' : '10000')
                                : rec.investment_type === 'SWP'
                                  ? '10000'
                                  : rec.investment_type === 'SIF'
                                    ? '1000000'
                                    : '50000'
                          }
                          required
                        />
                      </div>

                      <div>
                        <Label>
                          {rec.investment_type === 'STP' ? 'STP Period (Months) *' : 'Tenure (Months) *'}
                        </Label>
                        <input
                          type="number"
                          value={rec.tenure_months}
                          onChange={(e) => updateRecommendation(index, 'tenure_months', e.target.value)}
                          className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="12"
                          min="1"
                          required
                        />
                        {rec.investment_type === 'STP' && (
                          <p className="text-xs text-slate-500 mt-1">Duration for which transfers happen</p>
                        )}
                      </div>

                      {rec.investment_type === 'STP' && (
                        <div>
                          <Label>Total Holding Period (Months) *</Label>
                          <input
                            type="number"
                            value={rec.holding_period_months || ''}
                            onChange={(e) => updateRecommendation(index, 'holding_period_months', e.target.value)}
                            className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="60"
                            min={rec.tenure_months || 1}
                            required
                          />
                          <p className="text-xs text-slate-500 mt-1">Total time invested including post-STP hold (must be ≥ STP period)</p>
                        </div>
                      )}

                      <div>
                        <Label>
                          {rec.investment_type === 'STP' ? 'Target Fund Return (% p.a.) *' : 'Expected Return (% p.a.) *'}
                        </Label>
                        <input
                          type="number"
                          step="0.1"
                          value={rec.expected_return}
                          onChange={(e) => updateRecommendation(index, 'expected_return', e.target.value)}
                          className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="12.5"
                          required
                        />
                        {rec.investment_type === 'STP' && (
                          <p className="text-xs text-slate-500 mt-1">Expected return on target (equity) fund</p>
                        )}
                      </div>
                    </div>

                    {/* Scheme Returns & Benchmark section */}
                    <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg" data-testid={`returns-section-${index}`}>
                      <h5 className="font-semibold text-slate-900 mb-1">
                        {(rec.investment_type === 'STP' || rec.investment_type === 'SWP')
                          ? 'Target Scheme Returns & Benchmark Comparison'
                          : 'Scheme Returns & Benchmark Comparison'}
                      </h5>
                      <p className="text-xs text-slate-500 mb-4">
                        Optional — enter historical CAGR for the scheme and its benchmark to display a side-by-side comparison in the proposal.
                      </p>

                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label>
                            {(rec.investment_type === 'STP' || rec.investment_type === 'SWP')
                              ? 'Target Scheme Name'
                              : 'Scheme Name (as it appears in returns table)'}
                          </Label>
                          <input
                            type="text"
                            value={rec.target_scheme_name}
                            onChange={(e) => updateRecommendation(index, 'target_scheme_name', e.target.value)}
                            placeholder="E.g., HDFC Flexi Cap Fund Growth Plan"
                            className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid={`target-scheme-name-${index}`}
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Optional — defaults to the main Scheme Name if left blank.
                          </p>
                        </div>
                        <div>
                          <Label>Fund Inception Date</Label>
                          <input
                            type="date"
                            value={rec.fund_inception_date}
                            onChange={(e) => updateRecommendation(index, 'fund_inception_date', e.target.value)}
                            className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid={`fund-inception-date-${index}`}
                          />
                        </div>
                        <div>
                          <Label>Benchmark Name</Label>
                          <input
                            type="text"
                            value={rec.benchmark_name}
                            onChange={(e) => updateRecommendation(index, 'benchmark_name', e.target.value)}
                            placeholder="E.g., NIFTY 500 TRI"
                            className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid={`benchmark-name-${index}`}
                          />
                        </div>
                      </div>

                      {/* Scheme Returns row */}
                      <p className="text-sm font-semibold text-slate-700 mb-2">
                        {(rec.investment_type === 'STP' || rec.investment_type === 'SWP') ? 'Target Scheme Returns (% CAGR)' : 'Scheme Returns (% CAGR)'}
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        {[
                          { field: 'returns_1y', label: '1 Year' },
                          { field: 'returns_3y', label: '3 Year' },
                          { field: 'returns_5y', label: '5 Year' },
                          { field: 'returns_since_inception', label: 'Since Inception' },
                        ].map(({ field, label }) => (
                          <div key={field}>
                            <Label className="text-xs">{label}</Label>
                            <input
                              type="number"
                              step="0.01"
                              value={rec[field]}
                              onChange={(e) => updateRecommendation(index, field, e.target.value)}
                              placeholder="0.00"
                              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              data-testid={`scheme-${field}-${index}`}
                            />
                          </div>
                        ))}
                      </div>

                      {/* Benchmark Returns row */}
                      <p className="text-sm font-semibold text-slate-700 mb-2">Benchmark Returns (% CAGR)</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { field: 'bench_1y', label: '1 Year' },
                          { field: 'bench_3y', label: '3 Year' },
                          { field: 'bench_5y', label: '5 Year' },
                          { field: 'bench_since_inception', label: 'Since Inception' },
                        ].map(({ field, label }) => (
                          <div key={field}>
                            <Label className="text-xs">{label}</Label>
                            <input
                              type="number"
                              step="0.01"
                              value={rec[field]}
                              onChange={(e) => updateRecommendation(index, field, e.target.value)}
                              placeholder="0.00"
                              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              data-testid={`benchmark-${field}-${index}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Source Scheme Returns section — STP and SWP */}
                    {(rec.investment_type === 'STP' || rec.investment_type === 'SWP') && (
                      <div className="mt-4 p-4 bg-amber-50 border border-amber-100 rounded-lg" data-testid={`source-returns-section-${index}`}>
                        <h5 className="font-semibold text-slate-900 mb-1">Source Scheme Returns & Benchmark Comparison</h5>
                        <p className="text-xs text-slate-500 mb-4">
                          {rec.investment_type === 'STP'
                            ? 'Enter historical CAGR for the STP source (debt) scheme and its benchmark. Optional.'
                            : 'Enter historical CAGR for the SWP source (existing) scheme and its benchmark. Optional.'}
                        </p>

                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <Label>Source Scheme Name</Label>
                            <input
                              type="text"
                              value={rec.source_scheme_name}
                              onChange={(e) => updateRecommendation(index, 'source_scheme_name', e.target.value)}
                              placeholder="E.g., HDFC Liquid Fund"
                              className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              data-testid={`source-scheme-name-${index}`}
                            />
                          </div>
                          <div>
                            <Label>Source Fund Inception Date</Label>
                            <input
                              type="date"
                              value={rec.source_fund_inception_date}
                              onChange={(e) => updateRecommendation(index, 'source_fund_inception_date', e.target.value)}
                              className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              data-testid={`source-fund-inception-date-${index}`}
                            />
                          </div>
                          <div>
                            <Label>Source Benchmark Name</Label>
                            <input
                              type="text"
                              value={rec.source_benchmark_name}
                              onChange={(e) => updateRecommendation(index, 'source_benchmark_name', e.target.value)}
                              placeholder="E.g., CRISIL Composite Debt Index"
                              className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              data-testid={`source-benchmark-name-${index}`}
                            />
                          </div>
                          <div>
                            <Label>Source Exit Load (%)</Label>
                            <input
                              type="number"
                              step="0.01"
                              value={rec.source_exit_load_percent}
                              onChange={(e) => updateRecommendation(index, 'source_exit_load_percent', e.target.value)}
                              placeholder="0.50"
                              className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              data-testid={`source-exit-load-percent-${index}`}
                            />
                            <p className="text-xs text-slate-500 mt-1">Leave blank if Nil.</p>
                          </div>
                          <div>
                            <Label>Source Exit Load Applicable Within (Days)</Label>
                            <input
                              type="number"
                              value={rec.source_exit_load_days}
                              onChange={(e) => updateRecommendation(index, 'source_exit_load_days', e.target.value)}
                              placeholder="E.g., 90"
                              className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              data-testid={`source-exit-load-days-${index}`}
                            />
                          </div>
                          <div>
                            <Label>Source Scheme Expense Ratio (%)</Label>
                            <input
                              type="number"
                              step="0.01"
                              value={rec.source_expense_ratio || ''}
                              onChange={(e) => updateRecommendation(index, 'source_expense_ratio', e.target.value)}
                              placeholder="e.g., 1.10"
                              className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              data-testid={`source-expense-ratio-${index}`}
                            />
                            <p className="text-xs text-slate-500 mt-1">Total Expense Ratio (Regular Plan) of the source scheme.</p>
                          </div>
                        </div>

                        <p className="text-sm font-semibold text-slate-700 mb-2">Source Scheme Returns (% CAGR)</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                          {[
                            { field: 'source_returns_1y', label: '1 Year' },
                            { field: 'source_returns_3y', label: '3 Year' },
                            { field: 'source_returns_5y', label: '5 Year' },
                            { field: 'source_returns_since_inception', label: 'Since Inception' },
                          ].map(({ field, label }) => (
                            <div key={field}>
                              <Label className="text-xs">{label}</Label>
                              <input
                                type="number"
                                step="0.01"
                                value={rec[field]}
                                onChange={(e) => updateRecommendation(index, field, e.target.value)}
                                placeholder="0.00"
                                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                data-testid={`source-scheme-${field}-${index}`}
                              />
                            </div>
                          ))}
                        </div>

                        <p className="text-sm font-semibold text-slate-700 mb-2">Source Benchmark Returns (% CAGR)</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {[
                            { field: 'source_bench_1y', label: '1 Year' },
                            { field: 'source_bench_3y', label: '3 Year' },
                            { field: 'source_bench_5y', label: '5 Year' },
                            { field: 'source_bench_since_inception', label: 'Since Inception' },
                          ].map(({ field, label }) => (
                            <div key={field}>
                              <Label className="text-xs">{label}</Label>
                              <input
                                type="number"
                                step="0.01"
                                value={rec[field]}
                                onChange={(e) => updateRecommendation(index, field, e.target.value)}
                                placeholder="0.00"
                                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                                data-testid={`source-benchmark-${field}-${index}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex gap-4">
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                disabled={loading}
              >
                {loading
                  ? (isEditMode ? 'Saving...' : 'Creating...')
                  : (isEditMode ? 'Save Changes' : 'Create Proposal')}
              </Button>
              <Link to="/admin/dashboard">
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProposal;
